import config from 'config';
import { defineMessage } from 'react-intl';
import { Options } from 'ky';
import * as N from 'Common/notifications';
import { useAuthenticatedApiClient } from 'utils/apiClient/base';
import { SaveTax } from 'Settings/types';
import ownFetch from 'utils/own-fetch';
import { AccountPersistInfo } from 'Internal.CommonAPI';
import { useLocalStorageState } from 'ahooks';
import { Tax } from 'CoreApi.Settings_V12';
import { OrganizationToken, PortalSessionResponse } from './types';

const genericClientOpts: Options = {
  prefixUrl: `${config.CORE_URL}/api`,
};
// eslint-disable-next-line import/prefer-default-export
export const useBusinessApi = () => {
  const authenticatedApiClient = useAuthenticatedApiClient();
  const [account] = useLocalStorageState<AccountPersistInfo>('account');
  const genericApiClient = authenticatedApiClient.extend(genericClientOpts);
  const headers = {
    Authorization: `Token ${account?.userToken}`,
    'Content-Type': 'application/json',
  };
  return {
    saveTax: async (payload: SaveTax) => {
      const { id, name, tax_rate, keep_gross } = payload;

      if (!id && !keep_gross) {
        return genericApiClient
          .extend({ timeout: false })
          .post('taxes/', {
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ name, tax_rate }),
          })
          .json<Tax>();
      }
      N.publishUIMessage(
        'loading',
        defineMessage({
          defaultMessage:
            'We are processing your request to change your tax "{name}". It will be finished in few instants.',
        }),
        {
          values: { name: payload.name },
          duration: 8,
        }
      );
      return authenticatedApiClient
        .extend({ timeout: false })
        .put(`${config.CORE_URL}/api/taxes/${id}/`, {
          headers,
          body: JSON.stringify({ name, tax_rate, keep_gross }),
        })
        .json();
    },

    deleteTax: async ({ id, new_tax }: { id: number; new_tax: number }) =>
      authenticatedApiClient
        .delete(`${config.CORE_URL}/api/taxes/${id}/`, {
          headers,
          body: JSON.stringify({
            new_tax,
          }),
        })
        .json(),

    getOrganizationToken: () =>
      genericApiClient
        .get('auth/organization/token/')
        .json<OrganizationToken>(),

    getLatestSubscription: (
      org_token: string | undefined,
      merchant_id: string | undefined
    ) => {
      if (!org_token || !merchant_id) return null;
      const url = config.SUBSCRIPTION_APP_URL;
      return ownFetch(
        `${url}/v3/subscription/merchants/${merchant_id}/latest_subscription`,
        'GET',
        null,
        {
          Authorization: `Bearer ${org_token}`,
        }
      );
    },

    getPortalSession: async ({
      org_token,
      merchant_id,
    }: {
      org_token: string | undefined;
      merchant_id: string | undefined;
    }) => {
      if (!org_token || !merchant_id) return null;
      const url = config.SUBSCRIPTION_APP_URL;
      const data: PortalSessionResponse = await ownFetch(
        `${url}/v3/subscription/portal_sessions`,
        'POST',
        {
          merchant_id,
        },
        {
          Authorization: `Bearer ${org_token}`,
          'Content-Type': 'application/json',
        }
      );
      return data;
    },
    getLatestSubscriptionWithPosAccountId: (
      account_id: number | undefined,
      org_token: string | undefined
    ) => {
      if (!org_token || !account_id) return null;
      const url = config.SUBSCRIPTION_APP_URL;
      return ownFetch(
        `${url}/v3/subscription/latest_subscription?pos_account_id=${account_id}`,
        'GET',
        null,
        {
          Authorization: `Bearer ${org_token}`,
        }
      );
    },
  };
};
